const primitives = {
  colors: {
    brand: {
      primary: '#E50914',
      primaryLight: '#FF2A33',
      primaryDark: '#B00710',
      secondary: '#B00710',
      secondaryLight: '#E50914',
      secondaryDark: '#80040A',
      // Light-mode reds run deeper for contrast on light surfaces (v2)
      primaryOnLight: '#B20710',
      primaryDarkOnLight: '#8A050C',
    },
    neutral: {
      white: '#FFFFFF',
      black: '#000000',
      gray900: '#222222',
      gray800: '#333333',
      gray600: '#4f4f4f',
      gray500: '#787878',
      gray300: '#d3d3d3',
      gray200: '#e0e0e0',
      warm100: '#e9e3d8',
    },
    surface: {
      // Dark palette (v3 Glass×Material: bg, bg-2 + solid card steps)
      ink: '#0A0A0A',
      carbon: '#101012',
      graphite: '#141417',
      raise: '#1A1A1E',
      paper: '#FFFFFF',
      ash: '#8C8C8C',
      ashDim: '#5A5A5C',
      // Dark text hierarchy (v3: text, text-2, text-3, text-4)
      text2: '#CFCFD2',
      text3: '#8C8C8C',
      text4: '#5A5A5C',
      // Light palette equivalents (v3: bg, bg-2 + solid white cards)
      inkLight: '#F4F4F5',
      carbonLight: '#EDEDEF',
      graphiteLight: '#FFFFFF',
      raiseLight: '#F7F7F7',
      paperLight: '#0A0A0A',
      ashLight: '#6E6E73',
      ashDimLight: '#9C9CA1',
      // Light text hierarchy (v3: text, text-2, text-3, text-4)
      text2Light: '#3C3C40',
      text3Light: '#6E6E73',
      text4Light: '#9C9CA1',

      secondaryButton: '#242424',
      secondaryButtonHover: '#1C1C1C',
      voteRailLight: '#F5F5F5',
      voteRailDark: '#121212',
      voteRailNeutralDark: '#1C1C1C',
      downvote: '#5A5A5A',
    },
    status: {
      error: '#d32f2f',
      destructive: '#E26D6D',
      // v2 status colors (dark surfaces)
      success: '#27C26E',
      warning: '#F2B61C',
      info: '#3B9DFF',
      // v2 status colors (light surfaces — deeper for contrast)
      successLight: '#1AA85C',
      warningLight: '#C9920A',
      infoLight: '#1E7FE0',
    },
    alpha: {
      white70: 'rgba(255,255,255,0.7)',
      white60: 'rgba(255,255,255,0.6)',
      white50: 'rgba(255,255,255,0.5)',
      black80: 'rgba(0,0,0,0.8)',
      black10: 'rgba(0,0,0,0.1)',
      black08: 'rgba(0,0,0,0.08)',
      black04: 'rgba(0, 0, 0, 0.04)',
      dividerDark: 'rgba(90,90,90,0.4)',
      messageDark: 'rgba(28,28,28,0.6)',
      primary50: 'rgba(229,9,20,0.5)',
      primary04: 'rgba(229,9,20,0.04)',
      destructive10: 'rgba(226, 109, 109, 0.1)',
      postHoverDark: 'rgba(36,36,36,0.9)',
      // v3 red accents — accent-dim fill + accent-line hairline (guide tokens)
      redSoft: 'rgba(229,9,20,0.14)',
      redLine: 'rgba(229,9,20,0.38)',
      redSoftLight: 'rgba(178,7,16,0.10)',
      redLineLight: 'rgba(178,7,16,0.34)',
      // Frosted-glass bar tints — translucent so scrolled content shows through
      glassDark: 'rgba(20,20,20,0.72)',
      glassLight: 'rgba(247,247,247,0.72)',
      glassBorderDark: 'rgba(255,255,255,0.08)',
      glassBorderLight: 'rgba(0,0,0,0.08)',
    },
    // v3 Glass×Material recipe — glass belongs on floating surfaces only
    // (bars, dialogs, sheets, menus, popovers); content cards stay solid.
    glass: {
      dark: {
        bg1: 'rgba(24,24,27,0.52)',
        bg2: 'rgba(30,30,34,0.62)',
        bg3: 'rgba(38,38,43,0.72)',
        line1: 'rgba(255,255,255,0.12)',
        line2: 'rgba(255,255,255,0.20)',
        spec: 'rgba(255,255,255,0.09)',
        shadow: '0 14px 44px rgba(0,0,0,0.46)',
        shadow2: '0 22px 64px rgba(0,0,0,0.55)',
        scrim: 'rgba(0,0,0,0.5)',
      },
      light: {
        bg1: 'rgba(255,255,255,0.55)',
        bg2: 'rgba(255,255,255,0.68)',
        bg3: 'rgba(255,255,255,0.82)',
        line1: 'rgba(10,10,10,0.09)',
        line2: 'rgba(10,10,10,0.16)',
        spec: 'rgba(255,255,255,0.9)',
        shadow: '0 14px 40px rgba(120,10,16,0.10)',
        shadow2: '0 22px 60px rgba(120,10,16,0.14)',
        scrim: 'rgba(10,10,10,0.32)',
      },
    },
    // Ambient aurora blobs the glass refracts (fixed background layer).
    // Order: [a, b, c] — a: primary red (top-left blob), b: soft red (right
    // blob), c: neutral (bottom blob). Same colors feed the flat mobile
    // radial-gradient fallback, so keep the two in sync by editing here only.
    aurora: {
      dark: ['rgba(178,7,16,0.34)', 'rgba(229,9,20,0.14)', 'rgba(60,60,80,0.30)'],
      light: ['rgba(229,9,20,0.16)', 'rgba(178,7,16,0.08)', 'rgba(140,140,160,0.22)'],
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', sans-serif",
    // v2 type system: Archivo Black (display/brand), Archivo (UI/sans), Inter (body), JetBrains Mono (meta)
    displayFontFamily: "'Archivo Black', 'Archivo', sans-serif",
    brandFontFamily: "'Archivo Black', 'Archivo', sans-serif",
    sansFontFamily: "'Archivo', 'Inter', sans-serif",
    monoFontFamily: "'JetBrains Mono', source-code-pro, Menlo, Monaco, monospace",
    codeFontFamily: "'JetBrains Mono', source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
    sizes: {
      micro: '0.7rem',
      caption: '0.8rem',
      captionLarge: '0.85rem',
      meta: '0.9rem',
      metaLarge: '0.95rem',
      body: '1rem',
      bodyLarge: '1.05rem',
      lead: '1.2rem',
      titleLarge: '1.25rem',
      brand: '1.3em',
      mobileTitle: '1.5em',
      bodyEmphasis: '1.1em',
      inboxEmptyIcon: '3rem',
      searchEmptyIcon: '4em',
      h6: '18px',
      subtitle: '16px',
      bodyMui: '15px',
      bodySmallMui: '12px',
      h1: '2rem',
      h2: '1.75rem',
      // Post card type scale (component library v2 · Feed Post Card)
      postTitle: '14.5px',
      postMeta: '12.5px',
      postBody: '14.5px',
      emptyStateIcon: '8em',
      achievementIcon: '40px',
      achievementHeroIcon: '56px',
    },
    weights: {
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
      extraBold: 800,
    },
    lineHeights: {
      compact: 1.3,
      title: 1.4,
      body: 1.5,
      postBody: 1.55,
    },
    letterSpacing: {
      normal: 'normal',
    },
    postTypeIcon: {
      xs: 100,
      sm: 130,
      md: 150,
    },
    postTypeIconExpanded: {
      xs: 120,
      sm: 160,
      md: 180,
    },
  },
  spacing: {
    none: '0px',
    hairline: '1px',
    xxs: '2px',
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
    section: '40px',
    page: '48px',
    jumbo: '64px',
    inlineTight: '0.1em',
    inlineCompact: '0.2em',
    inline: '0.25em',
    inlineRelaxed: '0.3em',
    stackTight: '0.5em',
    stackCompact: '0.6em',
    stack: '0.7em',
    stackRelaxed: '0.8em',
    stackNormal: '1em',
    stackLarge: '1.5em',
    stackXLarge: '1.8em',
    formSwitchGap: '1.2em',
    formSubmitGap: '1.4em',
    nestedCommentIndent: '2em',
    loadingOffset: '12em',
    emptyStateOffset: '5em',
    emptyStateTopOffset: '20%',
    sectionInlineMargin: '0.3em 0',
    sectionVerticalPadding: '0.5em 0',
    fieldControlGap: '5px',
    iconGap: '7px',
    authMobileOffset: '80px',
    safeAreaTop: 'max(0.5em, env(safe-area-inset-top))',
  },
  radii: {
    none: '0px',
    control: '4px',
    image: '8px',
    card: '12px',
    large18: '18px',
    cardLarge: '24px',
    cardXLarge: '36px',
    input: '20px',
    pill: '9999px',
    circle: '50%',
    // v3 Material-3 shape scale (guide: sh-xs … sh-full)
    shXs: '4px',
    shSm: '8px',
    shMd: '12px',
    shLg: '16px',
    shXl: '28px',
    shFull: '999px',
  },
  borders: {
    widthThin: '1px',
    widthNormal: '2px',
    widthThick: '4px',
    style: 'solid',
  },
  shadows: {
    none: 'none',
    level1: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    level2: '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    level3: '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    level4: '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    mobileCardLight: '0 1px 3px rgba(0,0,0,0.12)',
    mobileCardDark: '0 1px 3px rgba(0,0,0,0.3)',
    cardLight: '0 1px 4px rgba(20,20,20,0.04)',
    cardDark: '0 2px 8px rgba(0,0,0,0.5)',
    cardHoverLight: '0 6px 20px rgba(20,20,20,0.08)',
    cardHoverSoftLight: '0 6px 20px rgba(20,20,20,0.06)',
    cardHoverDark: '0 4px 12px rgba(0,0,0,0.3)',
    cardHoverStrongDark: '0 8px 30px rgba(0,0,0,0.6)',
    expandedCardLight: '0 2px 12px rgba(20,20,20,0.08)',
    expandedCardHoverLight: '0 8px 32px rgba(20,20,20,0.12)',
    // v2 overlay/modal shadow
    overlayDark: '0 18px 50px rgba(0,0,0,0.55)',
    overlayLight: '0 18px 50px rgba(0,0,0,0.12)',
  },
  animations: {
    instant: '0ms',
    fast: '150ms',
    quick: '200ms',
    normal: '300ms',
    slow: '400ms',
    deliberate: '600ms',
    easing: 'ease',
    easingInOut: 'ease-in-out',
    easingOut: 'ease-out',
    // v3 Material-3 motion (guide: --ease / --dur)
    easeM3: 'cubic-bezier(.2,0,0,1)',
    durM3: '280ms',
    hoverScale: 'scale(1.05)',
    hoverScaleStrong: 'scale(1.08)',
    pulseStart: 'scale(0.5)',
    pulseEnd: 'scale(1)',
  },
  layout: {
    buttonHeight: '44px',
    inputHeight: '44px',
    cardPadding: '16px',
    sectionGap: '24px',
    containerWidth: '1280px',
    mobileNavHeight: '64px',
    topBarHeight: '64px',
    compactAvatar: '32px',
    avatar: '48px',
    voteRailWidth: '48px',
    thumbnail: '72px',
    settingsRowHeight: '72px',
    compactControl: '40px',
    imagePreview: '150px',
    contentMaxWidth: '700px',
    searchMaxWidth: '600px',
    mediaPreviewHeight: '400px',
    mobileMediaPreviewHeight: '200px',
    pageMinHeight: '90vh',
    viewportHeight: '100vh',
    full: '100%',
    bottomNavSafePadding: 'max(64px, calc(64px + env(safe-area-inset-bottom)))',
    breakpoints: {
      xs: 0,
      sm: 650,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  zIndex: {
    background: 0,
    sticky: 5,
    overlay: 1000,
  },
  effects: {
    contrastLight: 'contrast(120%)',
    contrastDark: 'contrast(80%)',
    // Shared frosted-glass blur (v3 guide recipe) — floating surfaces only
    glassBlur: 'blur(24px) saturate(1.5)',
  },
  // v3 medal ring gradient stops (achievement tiers) and store rarity accents
  medals: {
    bronze: ['#8A5A32', '#C77B4A'],
    silver: ['#7E838E', '#C6CBD6'],
    gold: ['#9A6E14', '#E8B341'],
    goat: ['#B20710', '#E50914', '#FF5A63'],
  },
  rarity: {
    rare: { fg: '#5B96F0', bg: 'rgba(42,111,219,0.14)' },
    epic: { fg: '#A78BFA', bg: 'rgba(139,92,246,0.14)' },
    leg: { fg: '#E8B341', bg: 'rgba(232,179,65,0.13)' },
  },
} as const;

const lightColors = {
  textPrimary: primitives.colors.surface.paperLight,
  textSecondary: primitives.colors.surface.ashLight,
  textHeading: primitives.colors.surface.paperLight,
  textAction: primitives.colors.surface.ashLight,
  voteCount: primitives.colors.surface.ashLight,
  textMuted: primitives.colors.alpha.black80,
  // v2 text hierarchy
  text2: primitives.colors.surface.text2Light,
  text3: primitives.colors.surface.text3Light,
  text4: primitives.colors.surface.text4Light,
  backgroundDefault: primitives.colors.surface.inkLight,
  backgroundPaper: primitives.colors.surface.carbonLight,
  backgroundCard: primitives.colors.surface.graphiteLight,
  backgroundCardHover: primitives.colors.surface.raiseLight,
  divider: primitives.colors.surface.ashDimLight,
  cardBorder: primitives.colors.glass.light.line1,
  voteRail: primitives.colors.surface.voteRailLight,
  voteRailNeutral: primitives.colors.surface.voteRailLight,
  messageIncoming: primitives.colors.neutral.gray200,
  logoPrimary: primitives.colors.brand.primary,
  logoSecondary: primitives.colors.brand.secondary,
  // v3: flat backgrounds — the aurora layer supplies the color interest
  appGradient: primitives.colors.surface.inkLight,
  webGradient: primitives.colors.surface.inkLight,
  // v2 mode-aware brand + accents (deeper red on light surfaces)
  primary: primitives.colors.brand.primaryOnLight,
  primaryDark: primitives.colors.brand.primaryDarkOnLight,
  redSoft: primitives.colors.alpha.redSoftLight,
  redLine: primitives.colors.alpha.redLineLight,
  focusRing: `0 0 0 3px ${primitives.colors.alpha.redSoftLight}`,
  success: primitives.colors.status.successLight,
  warning: primitives.colors.status.warningLight,
  info: primitives.colors.status.infoLight,
  glassBackground: primitives.colors.glass.light.bg1,
  glassBorder: primitives.colors.glass.light.line1,
  // v3 glass recipe (floating surfaces only)
  glass1: primitives.colors.glass.light.bg1,
  glass2: primitives.colors.glass.light.bg2,
  glass3: primitives.colors.glass.light.bg3,
  glassLine: primitives.colors.glass.light.line1,
  glassLine2: primitives.colors.glass.light.line2,
  glassSpec: primitives.colors.glass.light.spec,
  glassShadow: primitives.colors.glass.light.shadow,
  glassShadow2: primitives.colors.glass.light.shadow2,
  scrim: primitives.colors.glass.light.scrim,
  aurora: primitives.colors.aurora.light,
  stateLayer: '10,10,10',
} as const;

const darkColors = {
  textPrimary: primitives.colors.surface.paper,
  textSecondary: primitives.colors.surface.ash,
  textHeading: primitives.colors.surface.paper,
  textAction: primitives.colors.surface.ash,
  voteCount: primitives.colors.surface.ash,
  textMuted: primitives.colors.surface.ashDim,
  // v2 text hierarchy
  text2: primitives.colors.surface.text2,
  text3: primitives.colors.surface.text3,
  text4: primitives.colors.surface.text4,
  backgroundDefault: primitives.colors.surface.ink,
  backgroundPaper: primitives.colors.surface.carbon,
  backgroundCard: primitives.colors.surface.graphite,
  backgroundCardHover: primitives.colors.surface.raise,
  divider: primitives.colors.surface.ashDim,
  cardBorder: primitives.colors.glass.dark.line1,
  voteRail: primitives.colors.surface.voteRailDark,
  voteRailNeutral: primitives.colors.surface.voteRailNeutralDark,
  messageIncoming: primitives.colors.alpha.messageDark,
  logoPrimary: primitives.colors.brand.primary,
  logoSecondary: primitives.colors.brand.secondary,
  // v3: flat backgrounds — the aurora layer supplies the color interest
  appGradient: primitives.colors.surface.ink,
  webGradient: primitives.colors.surface.ink,
  // v2 mode-aware brand + accents
  primary: primitives.colors.brand.primary,
  primaryDark: primitives.colors.brand.primaryDark,
  redSoft: primitives.colors.alpha.redSoft,
  redLine: primitives.colors.alpha.redLine,
  focusRing: `0 0 0 3px ${primitives.colors.alpha.redSoft}`,
  success: primitives.colors.status.success,
  warning: primitives.colors.status.warning,
  info: primitives.colors.status.info,
  glassBackground: primitives.colors.glass.dark.bg1,
  glassBorder: primitives.colors.glass.dark.line1,
  // v3 glass recipe (floating surfaces only)
  glass1: primitives.colors.glass.dark.bg1,
  glass2: primitives.colors.glass.dark.bg2,
  glass3: primitives.colors.glass.dark.bg3,
  glassLine: primitives.colors.glass.dark.line1,
  glassLine2: primitives.colors.glass.dark.line2,
  glassSpec: primitives.colors.glass.dark.spec,
  glassShadow: primitives.colors.glass.dark.shadow,
  glassShadow2: primitives.colors.glass.dark.shadow2,
  scrim: primitives.colors.glass.dark.scrim,
  aurora: primitives.colors.aurora.dark,
  stateLayer: '255,255,255',
} as const;

const generatedArtwork = {
  overlay: primitives.colors.alpha.black08,
  cornerRadius: '28',
  palettes: [
    ['#1F2933', '#374151', '#4B5563', '#6B7280'],
    ['#1C1917', '#292524', '#44403C', '#78716C'],
    ['#111827', '#1F2937', '#374151', '#4B5563'],
    ['#2A2A2A', '#3A3A3A', '#525252', '#737373'],
    ['#1B1F24', '#2D333B', '#444C56', '#636E7B'],
    ['#2C2A4A', '#4A478A', '#6B6AAE', '#8B8ACC'],
  ],
} as const;

export const tokens = {
  colors: {
    primary: primitives.colors.brand.primary,
    primaryLight: primitives.colors.brand.primaryLight,
    primaryDark: primitives.colors.brand.primaryDark,
    secondary: primitives.colors.brand.secondary,
    secondaryLight: primitives.colors.brand.secondaryLight,
    secondaryDark: primitives.colors.brand.secondaryDark,
    success: primitives.colors.status.success,
    warning: primitives.colors.status.warning,
    error: primitives.colors.status.error,
    info: primitives.colors.status.info,
    destructive: primitives.colors.status.destructive,
    textPrimary: lightColors.textPrimary,
    textSecondary: lightColors.textSecondary,
    backgroundDefault: lightColors.backgroundDefault,
    backgroundPaper: lightColors.backgroundPaper,
    backgroundCard: lightColors.backgroundCard,
    divider: lightColors.divider,
    voteDown: primitives.colors.surface.downvote,
    onPrimary: primitives.colors.neutral.white,
    secondaryButton: primitives.colors.surface.secondaryButton,
    secondaryButtonHover: primitives.colors.surface.secondaryButtonHover,
    destructiveHover: primitives.colors.alpha.destructive10,
    primarySubtle: primitives.colors.alpha.primary04,
    primary50: primitives.colors.alpha.primary50,
    iconDisabled: primitives.colors.alpha.white50,
    mediaBorder: primitives.colors.neutral.warm100,
    // v2 accents (defaults; overridden per-mode in getDesignTokens)
    text2: primitives.colors.surface.text2,
    text3: primitives.colors.surface.text3,
    text4: primitives.colors.surface.text4,
    redSoft: primitives.colors.alpha.redSoft,
    redLine: primitives.colors.alpha.redLine,
    focusRing: `0 0 0 3px ${primitives.colors.alpha.redSoft}`,
  },
  typography: {
    fontFamily: primitives.typography.fontFamily,
    h1Size: primitives.typography.sizes.h1,
    h2Size: primitives.typography.sizes.h2,
    h3Size: '3rem',
    h4Size: '2.125rem',
    h5Size: '1.5rem',
    h6Size: primitives.typography.sizes.h6,
    body1Size: primitives.typography.sizes.bodyMui,
    body2Size: primitives.typography.sizes.bodySmallMui,
    buttonSize: '0.875rem',
    fontWeightLight: primitives.typography.weights.light,
    fontWeightRegular: primitives.typography.weights.regular,
    fontWeightMedium: primitives.typography.weights.medium,
    fontWeightSemiBold: primitives.typography.weights.semiBold,
    fontWeightBold: primitives.typography.weights.bold,
    fontWeightExtraBold: primitives.typography.weights.extraBold,
    lineHeight: primitives.typography.lineHeights.body,
    letterSpacing: primitives.typography.letterSpacing.normal,
    ...primitives.typography,
  },
  spacing: primitives.spacing,
  radii: primitives.radii,
  borders: primitives.borders,
  shadows: primitives.shadows,
  animations: primitives.animations,
  layout: primitives.layout,
  zIndex: primitives.zIndex,
  effects: primitives.effects,
  artwork: generatedArtwork,
  medals: primitives.medals,
  rarity: primitives.rarity,
} as const;

export const getDesignTokens = (mode: 'light' | 'dark' = 'light') => {
  const modeColors = mode === 'dark' ? darkColors : lightColors;

  return {
    ...tokens,
    mode,
    colors: {
      ...tokens.colors,
      ...modeColors,
    },
    shadows: {
      ...tokens.shadows,
      mobileCard: mode === 'dark'
        ? primitives.shadows.mobileCardDark
        : primitives.shadows.mobileCardLight,
      card: mode === 'dark' ? primitives.shadows.cardDark : primitives.shadows.cardLight,
      cardHover: mode === 'dark'
        ? primitives.shadows.cardHoverDark
        : primitives.shadows.cardHoverLight,
      overlay: mode === 'dark'
        ? primitives.shadows.overlayDark
        : primitives.shadows.overlayLight,
    },
  } as const;
};

export type DesignTokens = ReturnType<typeof getDesignTokens>;

export default tokens;
